import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2, Minus, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, CATEGORY_LABELS, CATEGORY_PRICES, imageUri, SIZE_HEIGHTS, SIZES, SPEC_KEYS } from "@/lib/noleggio";
import { useAuth } from "@/lib/auth";
import { tr } from "@/lib/i18n";
import PhotoButtons from "@/components/PhotoButtons";

export default function AdminBikeForm() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.role !== "admin") return <Navigate to="/admin" replace />;
  return <Form />;
}

function Form() {
  const { id } = useParams();
  const isNew = id === "nuova";
  const nav = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", category: "gravel", description: "", description_en: "", image_url: "", price_day: isNew ? "40" : "", model_year: "" });
  const [specs, setSpecs] = useState({});
  const [stock, setStock] = useState({ S: 0, M: 0, L: 0, XL: 0 });
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState("");
  const [error, setError] = useState(null);

  const existing = useQuery({ queryKey: ["bike", id], queryFn: () => api(`/bikes/${id}`), enabled: !isNew });
  useEffect(() => {
    const b = existing.data;
    if (!b) return;
    setF({ name: b.name, category: b.category, description: b.description ?? "", description_en: b.description_en ?? "", image_url: b.image_url ?? "", price_day: String(b.price_day), model_year: b.model_year ?? "" });
    setSpecs(b.specs ?? {});
    setStock(b.stock);
  }, [existing.data]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setError(null);
    const pd = parseFloat(String(f.price_day).replace(",", "."));
    if (!f.name.trim() || !(pd > 0) || (f.model_year.trim() && !/^(19|20)\d{2}$/.test(f.model_year.trim()))) return setError("Controlla nome, prezzo e anno");
    setBusy(true);
    try {
      const cleanSpecs = Object.fromEntries(Object.entries(specs).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]));
      const body = { ...f, name: f.name.trim(), image_url: f.image_url.trim(), price_day: pd, model_year: f.model_year.trim(), stock, specs: cleanSpecs };
      await api(isNew ? "/admin/bikes" : `/admin/bikes/${id}`, { method: isNew ? "POST" : "PUT", body });
      qc.invalidateQueries();
      toast.success(isNew ? "Bici aggiunta" : "Bici aggiornata");
      nav("/admin");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const searchOnline = async () => {
    if (!f.name.trim()) return setError("Scrivi prima il nome del modello");
    setError(null);
    setSearching(true);
    try {
      const r = await api("/admin/bikes/lookup", { method: "POST", body: { name: f.name.trim(), category: f.category, year: f.model_year.trim() || null, exclude_image: f.image_url.trim() || null } });
      setF((p) => ({ ...p, image_url: r.image_url || p.image_url, description: r.description || p.description, description_en: r.description_en || p.description_en }));
      setSpecs((p) => ({ ...p, ...r.specs }));
      setSource(r.source_url);
      if (r.year && !r.year_confirmed) toast.info(`Nessun dato confermato per l'anno ${r.year}: controlla prima di salvare`);
      else toast.success(r.image_url ? "Dati trovati: controlla e salva" : "Dati trovati, ma nessuna foto");
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const step = (s, d) => setStock((p) => ({ ...p, [s]: Math.max(0, (p[s] ?? 0) + d) }));
  const input = (k, label, extra = {}) => (
    <label className="flex flex-col gap-1.5"><span className="mn-label">{label}</span>
      <input data-testid={`bike-form-${k.replace("_", "-")}`} className="mn-input" value={f[k]} onChange={(e) => set(k, e.target.value)} {...extra} /></label>
  );

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 pt-6 pb-10">
      <div className="flex items-center gap-2 mb-5">
        <button data-testid="bike-form-back" onClick={() => nav("/admin")} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white"><X /></button>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{isNew ? "Nuova bici" : "Modifica bici"}</h1>
      </div>
      <div className="mn-card p-5 flex flex-col gap-4">
        {f.image_url && <img src={imageUri(f.image_url)} alt="" className="h-48 w-full object-cover rounded-2xl bg-[var(--surface-3)]" data-testid="bike-form-preview" />}
        {input("name", "Nome", { placeholder: "Es. Raymon Soreno Comp" })}
        <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
          {input("model_year", "Anno (model year)", { placeholder: "2026", maxLength: 4, inputMode: "numeric" })}
          <button type="button" data-testid="bike-form-search-online" className="mn-btn mn-btn-ghost" disabled={searching || busy} onClick={searchOnline}>
            {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} {searching ? "Ricerca in corso…" : "Cerca di nuovo online"}
          </button>
        </div>
        <p className="text-xs text-[var(--muted)] -mt-2 break-all">{source ? `Fonte: ${source}` : "Cerca foto, descrizione e caratteristiche del modello: controlli tu e poi salvi."}</p>
        <div className="flex flex-col gap-1.5"><span className="mn-label">Categoria</span>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" data-testid={`bike-form-category-${c}`} className={`mn-chip ${f.category === c ? "mn-chip-on" : ""}`}
                onClick={() => setF((p) => ({ ...p, category: c, price_day: isNew ? String(CATEGORY_PRICES[c]) : p.price_day }))}>{CATEGORY_LABELS[c]}</button>
            ))}
          </div>
        </div>
        {input("price_day", "Prezzo al giorno (€)", { inputMode: "decimal" })}
        <div className="flex flex-col gap-1.5"><span className="mn-label">Quantità per taglia</span>
          <div className="bg-[var(--surface-2)] rounded-xl p-3 flex flex-col gap-2">
            {SIZES.map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div className="flex-1"><p className="font-extrabold">{s}</p><p className="text-xs text-[var(--muted)]">{SIZE_HEIGHTS[s]}</p></div>
                <button type="button" data-testid={`bike-form-stock-minus-${s}`} onClick={() => step(s, -1)} className="w-10 h-10 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center"><Minus size={18} /></button>
                <span data-testid={`bike-form-stock-${s}`} className="w-8 text-center text-lg font-extrabold">{stock[s] ?? 0}</span>
                <button type="button" data-testid={`bike-form-stock-plus-${s}`} onClick={() => step(s, 1)} className="w-10 h-10 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center"><Plus size={18} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5"><span className="mn-label">Foto</span>
          <PhotoButtons path="/admin/upload" prefix="bike-form-photo" successText="Foto caricata" onUploaded={(d) => set("image_url", d.url)} />
        </div>
        {input("image_url", "Oppure indirizzo della foto (URL)", { placeholder: "https://..." })}
        <label className="flex flex-col gap-1.5"><span className="mn-label">Descrizione</span>
          <textarea data-testid="bike-form-description" className="mn-input py-3 min-h-[100px]" value={f.description} onChange={(e) => set("description", e.target.value)} /></label>
        <label className="flex flex-col gap-1.5"><span className="mn-label">Descrizione in inglese</span>
          <textarea data-testid="bike-form-description-en" className="mn-input py-3 min-h-[100px]" value={f.description_en} onChange={(e) => set("description_en", e.target.value)} /></label>
        <div className="flex flex-col gap-1.5"><span className="mn-label">Caratteristiche</span>
          <div className="bg-[var(--surface-2)] rounded-xl p-3 grid sm:grid-cols-2 gap-3">
            {SPEC_KEYS.map((k) => (
              <label key={k} className="flex flex-col gap-1"><span className="text-xs text-[var(--muted)]">{tr(`spec_${k}`)}</span>
                <input data-testid={`bike-form-spec-${k}`} className="mn-input !bg-white" value={specs[k] ?? ""} onChange={(e) => setSpecs((p) => ({ ...p, [k]: e.target.value }))} /></label>
            ))}
          </div>
        </div>
        {error && <p data-testid="bike-form-error" className="text-sm font-semibold text-[var(--error)]">{error}</p>}
        <button data-testid="bike-form-save" className="mn-btn" disabled={busy || searching} onClick={save}>
          {busy && <Loader2 size={18} className="animate-spin" />}{isNew ? "Aggiungi bici" : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}
